import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const _storeTokens = (data) => {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    const userData = data.user;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    // Staff with MFA already enabled → need OTP verification
    if (res.data.mfa_required) {
      return { mfa_required: true, mfa_token: res.data.mfa_token, user_email: res.data.user_email };
    }
    // Staff without MFA → need first-time setup (QR code)
    if (res.data.mfa_setup_required) {
      return {
        mfa_setup_required: true,
        mfa_token: res.data.mfa_token,
        user_email: res.data.user_email,
        provisioning_uri: res.data.provisioning_uri,
        mfa_secret: res.data.mfa_secret,
      };
    }
    // Non-staff → direct login
    return _storeTokens(res.data);
  };

  const verifyMfaLogin = async (mfa_token, code) => {
    const res = await authAPI.mfaLoginVerify({ mfa_token, code });
    return _storeTokens(res.data);
  };

  const completeMfaSetup = async (mfa_token, code) => {
    const res = await authAPI.mfaSetupComplete({ mfa_token, code });
    return _storeTokens(res.data);
  };

  const register = async (email, password, full_name) => {
    const res = await authAPI.register({ email, password, full_name });
    localStorage.setItem('access_token', res.data.access_token);
    localStorage.setItem('refresh_token', res.data.refresh_token);
    const userData = res.data.user;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // All roles that can access the admin dashboard
  const ADMIN_ROLES = [
    'super_admin', 'admin', 'editor', 'producer', 'journalist',
    'photographer', 'designer', 'data_journalist', 'social_media_manager',
    'seo_specialist', 'ad_operations', 'subscription_manager', 'analytics_analyst',
    'hr_admin', 'finance_controller', 'legal_counsel', 'external_contributor', 'moderator'
  ];
  const isAdmin = user && ADMIN_ROLES.includes(user.role);
  const canPublish = user && ['super_admin', 'admin', 'editor', 'producer'].includes(user.role);
  const canModerate = user && ['super_admin', 'admin', 'moderator'].includes(user.role);
  const canManageUsers = user && ['super_admin', 'admin', 'hr_admin'].includes(user.role);
  const canViewAnalytics = user && ['super_admin', 'admin', 'editor', 'analytics_analyst', 'seo_specialist', 'finance_controller', 'subscription_manager', 'ad_operations'].includes(user.role);
  const canCreateContent = user && ['super_admin', 'admin', 'editor', 'producer', 'journalist', 'photographer', 'designer', 'data_journalist', 'social_media_manager', 'external_contributor'].includes(user.role);

  return (
    <AuthContext.Provider value={{
      user, loading, login, verifyMfaLogin, completeMfaSetup, register, logout, loadUser,
      isAdmin, canPublish, canModerate, canManageUsers, canViewAnalytics, canCreateContent
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
